import React, { useState, useEffect } from 'react';
import axios from '../../services/api';
import { toast } from 'react-toastify';
import { FaCog, FaTrash, FaTimes } from 'react-icons/fa';
import AssignSipModal from './modals/AssignStaffSipModal';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { useTranslation } from 'react-i18next';

const StaffSipAccount = () => {
  const { t } = useTranslation();
  const [selectedCommunity, setSelectedCommunity] = useState('');
  const [selectedSipStatus, setSelectedSipStatus] = useState('All SIP');
  const [publicServices, setPublicServices] = useState([]);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedPublicService, setSelectedPublicService] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [publicServiceToRemoveSip, setPublicServiceToRemoveSip] = useState(null);
  const [displayPublicServices, setDisplayPublicServices] = useState([]);
  const [searchKey, setSearchKey] = useState('');

  // Fetch communities on mount
  useEffect(() => {
    fetchCommunities();
  }, []);

  const fetchCommunities = async () => {
    try {
      const response = await axios.get('/communities');
      setCommunities(response.data.communities);
    } catch (error) {
      console.error(error);
    }
  };

  // Filter rooms for display
  // const displayPublicServices = publicServices.filter(publicService => {
  //   const matchesCommunity = selectedCommunity && publicService.communityId === selectedCommunity;
  //   const matchesSip = selectedSipStatus === 'All SIP' ||
  //     (selectedSipStatus === 'Assigned SIP' && publicService.sipAccount) ||
  //     (selectedSipStatus === 'Unassigned SIP' && !publicService.sipAccount);

  //   return matchesCommunity && matchesSip;
  // });

  useEffect(() => {
    fetchPublicServices();
  }, []);

  const fetchPublicServices = async () => {
    try {
      const response = await axios.get('/settings/staff-sip-settings');
      setPublicServices(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemoveSip = async (publicService) => {
    try {
      await axios.post('/settings/remove-staff-sip', { publicServiceId: publicService._id });
      toast.success(t('SIP account removed successfully'));
      fetchPublicServices();
      setIsConfirmOpen(false);
      setPublicServiceToRemoveSip(null);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setDisplayPublicServices(publicServices.filter(publicService => {
      const matchesCommunity = selectedCommunity && publicService.communityId === selectedCommunity;
      const matchesSip = selectedSipStatus === 'All SIP' ||
        (selectedSipStatus === 'Assigned SIP' && publicService.sipAccount) ||
        (selectedSipStatus === 'Unassigned SIP' && !publicService.sipAccount);
      const matchesSearch = searchKey.toLowerCase().trim() === '' ||
        (publicService?.communityName && publicService.communityName.toLowerCase().includes(searchKey.toLowerCase())) ||
        (publicService?.institutionName && publicService.institutionName.toLowerCase().includes(searchKey.toLowerCase())) ||
        (publicService?.contactName && publicService.contactName.toLowerCase().includes(searchKey.toLowerCase())) ||
        (publicService?.sipAccount && publicService.sipAccount.toLowerCase().includes(searchKey.toLowerCase()));
      return matchesCommunity && matchesSip && matchesSearch;
    }));
  }, [publicServices, selectedCommunity, selectedSipStatus, searchKey]);

  return (
    <div className="p-6 bg-gradient-to-br from-skin-base to-skin-secondary container-height app-text">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6 bg-skin-primary px-4 rounded-xl shadow-lg h-filter">
        <select
          value={selectedCommunity}
          onChange={(e) => {
            setSelectedCommunity(e.target.value);
          }}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm min-filter-7"
        >
          <option value="" hidden>{t('Select Community')}</option>
          {communities.map(community => (
            <option key={community._id} value={community._id}>{community.communityName}</option>
          ))}
        </select>

        <select
          value={selectedSipStatus}
          onChange={(e) => setSelectedSipStatus(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
        >
          <option value="All SIP">{t('All SIP')}</option>
          <option value="Assigned SIP">{t('Assigned SIP')}</option>
          <option value="Unassigned SIP">{t('Unassigned SIP')}</option>
        </select>

        <div className="relative flex-1" >
          <input
            type="text"
            placeholder={t('Search')}
            className="bg-skin-primary border border-skin-base/10 rounded-lg px-4 py-2 text-skin-base focus:outline-none focus:border-skin-accent transition-colors w-full"
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
          />
          {searchKey && (
            <button
              onClick={() => {
                setSearchKey('');
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-skin-base/50 hover:text-skin-base"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="relative overflow-x-auto overflow-y-auto h-table-height">
        <table className="w-full">
          <thead className="sticky top-0 z-30 bg-skin-primary shadow-sm">
            <tr className="border-b border-skin-base/10">
              <th className="sticky left-0 z-20 bg-skin-primary px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[50px]">
                {t('No.')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[200px]">
                {t('Community Name')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[200px]">
                {t('Institute Name')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[100px]">
                {t('Contact Name')}
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-skin-base min-w-[150px]">
                {t('SIP Account')}
              </th>
              <th className="sticky right-0 z-40 bg-skin-primary px-6 py-3 text-right text-sm font-semibold text-skin-base min-w-[100px]">
                {t('Actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {displayPublicServices.map((publicService, index) => (
              <tr key={publicService._id} className="border-t border-skin-base/10">
                <td className="sticky left-0 z-10 bg-skin-primary px-6 py-4 text-sm text-skin-base">
                  {index + 1}
                </td>
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                  {publicService.communityName}
                </td>
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                  {publicService.institutionName}
                </td>
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                  {publicService.contactName}
                </td>
                <td className="px-6 py-4 text-sm text-skin-base whitespace-nowrap">
                  {publicService.sipAccount || '-'}
                </td>
                <td className="sticky right-0 z-10 bg-skin-primary px-6 py-4 text-center">
                  <div className="flex items-center gap-2 justify-end">
                    {publicService.sipAccount && (
                      <button
                        onClick={() => {
                          setPublicServiceToRemoveSip(publicService);
                          setIsConfirmOpen(true);
                        }}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setSelectedPublicService(publicService);
                        setIsAssignModalOpen(true);
                      }}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <FaCog className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      <AssignSipModal
        isOpen={isAssignModalOpen}
        onClose={() => {
          setIsAssignModalOpen(false);
          setSelectedPublicService(null);
        }}
        publicService={selectedPublicService}
        onSuccess={() => {
          fetchPublicServices();
          setIsAssignModalOpen(false);
        }}
      />

      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => {
          setIsConfirmOpen(false);
          setPublicServiceToRemoveSip(null);
        }}
        onConfirm={() => handleRemoveSip(publicServiceToRemoveSip)}
        title={t('Remove SIP Account')}
        message={t('Are you sure you want to remove this SIP account?')}
        confirmText={t('Remove')}
        cancelText={t('Cancel')}
        type="danger"
      />
    </div>
  );
};

export default StaffSipAccount;